import type { CmsFieldSpec } from '../../lib/api/cms';

/**
 * Hilfsfunktionen für die einklappbaren Listeneinträge (siehe CollapsibleItemCard).
 *
 * Liegt bewusst neben der Komponente statt in ihr: sonst verliert die Datei den
 * Fast-Refresh-Support (eine Datei darf nur Komponenten exportieren).
 */

// --- Offen-Status ------------------------------------------------------------
// Die Einträge haben keine stabile Id, der Offen-Status hängt also am Index und
// muss bei jeder Strukturänderung mitwandern.

/** Offen-Status zweier Einträge tauschen (nach dem Verschieben). */
export function swapOpen(open: ReadonlySet<number>, a: number, b: number): Set<number> {
  const next = new Set(open);
  const hadA = open.has(a);
  const hadB = open.has(b);
  next.delete(a);
  next.delete(b);
  if (hadB) next.add(a);
  if (hadA) next.add(b);
  return next;
}

/** Eintrag entfernen: Index rausnehmen, alle dahinter um eins nach vorn ziehen. */
export function dropOpen(open: ReadonlySet<number>, idx: number): Set<number> {
  const next = new Set<number>();
  open.forEach((i) => {
    if (i < idx) next.add(i);
    else if (i > idx) next.add(i - 1);
  });
  return next;
}

// --- Kurzvorschau ------------------------------------------------------------

const HTML_ENTITIES: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
};

/** Rich-Text auf reinen Text reduzieren — nur für die Vorschau in der Kopfzeile. */
function toPlainText(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(nbsp|amp|lt|gt|quot|#39);/g, (m) => HTML_ENTITIES[m] ?? m)
    .replace(/\s+/g, ' ')
    .trim();
}

/** Textfelder, aus denen sich eine sinnvolle Vorschau ziehen lässt. */
const SUMMARY_TYPES = new Set(['text', 'textarea', 'richtext', 'url']);

/**
 * Erstes befülltes Textfeld eines Eintrags als Kurzvorschau.
 * Bild-, Toggle- und Auswahlfelder werden übersprungen — die sagen zugeklappt nichts.
 */
export function itemSummary(
  item: Record<string, unknown> | undefined,
  fields: CmsFieldSpec[],
  max = 90,
): string {
  if (!item) return '';
  for (const field of fields) {
    if (!SUMMARY_TYPES.has(field.type)) continue;
    const raw = item[field.key];
    if (typeof raw !== 'string') continue;
    const text = field.type === 'richtext' ? toPlainText(raw) : raw.trim();
    if (!text) continue;
    return text.length > max ? `${text.slice(0, max)}…` : text;
  }
  return '';
}
